import { useEffect, useState } from 'react';
import { useController } from 'react-hook-form';

import { Constant, Option } from 'types';

import type { SelectProps } from './SelectWithControl';

/** Marks the empty option this hook inserts (callers may have their own `value: ''` options). */
const EMPTY_OPTION_FLAG = 'emptyPlaceholder';

const useSelectWithControl = (props: SelectProps) => {
  const {
    options,
    defaultValue,
    control,
    name,
    asKeyValues,
    isMulti,
    hideEmptyOption,
    emptyOptionLabel,
  } = props;

  const [selectedValue, selectedValueSet] = useState<Option | Option[]>();
  const { field, fieldState } = useController({ control, name });

  if (!hideEmptyOption && emptyOptionLabel !== undefined && options) {
    // Opt-in: added once per options array and relabelled on re-render, so a language switch
    // updates it instead of leaving the old label next to the new one.
    const emptyOption: Option = {
      value: '',
      label: emptyOptionLabel,
      additional: { [EMPTY_OPTION_FLAG]: 'true' },
    };
    const emptyIndex = options.findIndex((o) => o.additional?.[EMPTY_OPTION_FLAG]);
    if (emptyIndex === -1) {
      options.unshift(emptyOption);
    } else {
      options.splice(emptyIndex, 1, emptyOption);
    }
  } else if (!hideEmptyOption) {
    options?.unshift({ value: '', label: 'Select...' });
  }
  const optionsFiltered = options?.filter(
    (optionValue, index, self) =>
      index === self.findIndex((t) => t.label === optionValue.label)
  );

  useEffect(() => {
    if (defaultValue) {
      selectedValueSet(options?.filter((c) => defaultValue === c.value)[0]);
    } else {
      selectedValueSet(undefined);
    }
  }, [defaultValue, options]);

  useEffect(() => {
    if (defaultValue) return;
    if ((field && field.value) || (field && field.value === '')) {
      const fieldValue = options?.filter((item: Option) => {
        if (isMulti) {
          if (asKeyValues) {
            return field.value.some((fieldItem: Constant) => fieldItem.key === item.value);
          }

          return field.value.includes(item.value);
        }

        if (asKeyValues) {
          return item.value === field.value.key;
        }
        return item.value === field.value;
      });

      selectedValueSet(fieldValue);
    }
  }, [field.value, fieldState.isDirty, options]);

  return {
    selectedValue,
    selectedValueSet,
    optionsFiltered,
  };
};

export { useSelectWithControl };
